/**
 * Mobile Menu Toggle Component
 * Handles mobile menu open/close functionality with icon switching
 */

class MobileMenuToggle extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    
    // SVG icons
    // The width/height attributes and max-width:none are load-bearing: base.css sets
    // `svg { max-width: 100%; height: auto }`, so an unsized SVG in this flex row
    // collapses to 0x0 whenever the `size-6` utility is missing from the page bundle.
    this.hamburgerIcon = `
      <svg width="24" height="24" style="max-width:none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" data-slot="icon" aria-hidden="true" class="size-6">
        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" stroke-linecap="" stroke-linejoin=""></path>
      </svg>
    `;

    this.closeIcon = `
      <svg width="24" height="24" style="max-width:none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" data-slot="icon" aria-hidden="true" class="size-6">
        <path d="M6 18 18 6M6 6l12 12" stroke-linecap="" stroke-linejoin="" />
      </svg>
    `;
  }

  connectedCallback() {
    this.render();
    this.bindEvents();
    
    // Listen for dialog close events to sync state
    this.listenForDialogClose();
  }

  render() {
    this.innerHTML = `
      <div class="flex shrink-0 lg:hidden">
        <button type="button" 
                class="p-2 bg-white rounded-md text-darkGreen mobile-menu-button" 
                aria-expanded="false">
          <span class="sr-only">Open menu</span>
          <div class="icon-container">
            ${this.hamburgerIcon}
          </div>
        </button>
      </div>
    `;
  }

  bindEvents() {
    const button = this.querySelector('.mobile-menu-button');
    if (button) {
      button.addEventListener('click', this.handleToggle.bind(this));
    }
  }

  handleToggle(event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
    
    const button = this.querySelector('.mobile-menu-button');
    const iconContainer = this.querySelector('.icon-container');
    const srText = this.querySelector('.sr-only');
    
    if (this.isOpen && button && iconContainer && srText) {
      // Opening menu
      document.body.classList.add('mobile-menu-open');
      button.setAttribute('aria-expanded', 'true');
      iconContainer.innerHTML = this.closeIcon;
      srText.textContent = 'Close menu';
      
      // Also trigger the modal show
      this.triggerModalShow();
    } else {
      // Closing menu
      this.closeMenu();
    }
  }

  closeMenu() {
    document.body.classList.remove('mobile-menu-open');
    
    const button = this.querySelector('.mobile-menu-button');
    const iconContainer = this.querySelector('.icon-container');
    const srText = this.querySelector('.sr-only');
    
    if (button && iconContainer && srText) {
      button.setAttribute('aria-expanded', 'false');
      iconContainer.innerHTML = this.hamburgerIcon;
      srText.textContent = 'Open menu';
    }
    
    this.isOpen = false;
  }

  triggerModalShow() {
    // Trigger the existing modal system
    const dialog = document.getElementById('mobile-menu');
    if (dialog) {
      // @ts-ignore
      if (typeof dialog.showModal === 'function') {
        // @ts-ignore
        dialog.showModal();
      }
    }
  }

  listenForDialogClose() {
    const dialog = document.getElementById('mobile-menu');
    if (dialog) {
      // Listen for dialog close events
      dialog.addEventListener('close', () => {
        if (this.isOpen) {
          this.closeMenu();
        }
      });
      
      // Also listen for backdrop clicks or escape key
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (!dialog.hasAttribute('open') && this.isOpen) {
              this.closeMenu();
            }
          }
        });
      });
      
      observer.observe(dialog, { attributes: true });
    }

    // Listen for clicks on close buttons inside the dialog
    document.addEventListener('click', (event) => {
      // @ts-ignore
      const closeButton = event.target && event.target.closest ? event.target.closest('[command="close"][commandfor="mobile-menu"]') : null;
      if (closeButton && this.isOpen) {
        this.closeMenu();
      }
    });
  }

  disconnectedCallback() {
    // Clean up body class when component is removed
    document.body.classList.remove('mobile-menu-open');
  }
}

// Register the custom element
if (!customElements.get('mobile-menu-toggle')) {
  customElements.define('mobile-menu-toggle', MobileMenuToggle);
}